import { DAY_TICKS, ORBIT_TICKS, levelInfo, orbitInfo, teamsInfo, type StationState } from "@station-keeping/sim";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { formatClock, formatDuration, formatNumber, formatPermyriad, t } from "../i18n";
import type { UiIconName } from "../pixel/icons";
import { Icon, PixelText, RollingNumber } from "./pixel";
import { Gauge } from "./primitives";

type Tone = "normal" | "warn" | "alert";

const TONE_COLORS: Record<Tone, string> = {
  normal: "var(--color-ink)",
  warn: "var(--color-warn)",
  alert: "var(--color-alert)",
};

/**
 * Une case du fronton : son libellé (et son pictogramme) au-dessus, sa valeur en dessous. En `compact`
 * (téléphone), le pictogramme seul précède la valeur ; le libellé reste lu et en info-bulle.
 */
function Cell({
  icon,
  label,
  detail,
  children,
  className = "",
  tone = "normal",
  compact = false,
}: {
  icon: UiIconName;
  label: string;
  detail?: string;
  children: ReactNode;
  className?: string;
  tone?: Tone;
  compact?: boolean;
}) {
  const color = tone === "normal" ? undefined : { color: TONE_COLORS[tone] };
  if (compact) {
    return (
      <div className={`flex min-w-0 items-center gap-1.5 ${className}`} title={detail ? `${label} — ${detail}` : label}>
        <Icon name={icon} className={tone === "normal" ? "text-dim" : ""} />
        <span className="sr-only">{label}</span>
        <span className="flex items-center gap-1.5" style={{ color: TONE_COLORS[tone] }}>
          {children}
        </span>
        {detail ? <span className="sr-only">{detail}</span> : null}
      </div>
    );
  }
  // Libellé en police bitmap, comme le « HI-SCORE » d'une borne : le pictogramme, puis le mot.
  return (
    <div className={`flex min-w-0 flex-col justify-between gap-1.5 ${className}`} title={detail ? `${label} — ${detail}` : label}>
      <span className="flex items-center gap-1.5 text-dim" style={color}>
        <Icon name={icon} />
        <PixelText text={label} />
      </span>
      <span className="flex items-center gap-2" style={{ color: TONE_COLORS[tone] }}>
        {children}
      </span>
      {detail ? <span className="sr-only">{detail}</span> : null}
    </div>
  );
}

function Divider() {
  return <span aria-hidden="true" className="hidden w-[var(--px)] self-stretch bg-[#1c2b56] md:block" />;
}

/**
 * La frise d'orbite, à ses durées exactes : quatre minutes de soleil, deux d'ombre, et un curseur qui avance
 * par paliers de quatre secondes.
 */
function OrbitFrieze({ tick, cells }: { tick: number; cells: number }) {
  const orbit = orbitInfo(tick);
  const ticksPerCell = ORBIT_TICKS / cells;
  const dayCells = DAY_TICKS / ticksPerCell;
  const cursor = Math.min(cells - 1, Math.floor(orbit.phase / ticksPerCell));
  const height = 7;
  const minute = 60 / ticksPerCell;
  let ticksPath = "";
  for (let x = minute; x < cells; x += minute) ticksPath += `M${Math.round(x)} 2h1v3h-1z`;
  return (
    <svg
      viewBox={`0 0 ${cells} ${height}`}
      aria-hidden="true"
      shapeRendering="crispEdges"
      className="pixel-svg"
      style={{ width: `calc(var(--px) * ${cells})`, height: `calc(var(--px) * ${height})` }}
    >
      {/* Le soleil, puis l'ombre de la planète. */}
      <rect x="0" y="2" width={dayCells} height="3" fill="#ffd166" />
      <rect x="0" y="2" width={dayCells} height="1" fill="#fff0b8" />
      <rect x={dayCells} y="2" width={cells - dayCells} height="3" fill="#3a4580" />
      <rect x={dayCells} y="4" width={cells - dayCells} height="1" fill="#262e5c" />
      <path d={ticksPath} fill="#04060d" opacity="0.45" />
      {/* Le curseur : la station sur son orbite. */}
      <rect x={cursor} y="0" width="1" height={height} fill="#04060d" />
      <rect x={cursor} y="1" width="1" height={height - 2} fill="#e8eeff" />
      <rect x={cursor - 1} y="0" width="3" height="1" fill="#e8eeff" />
    </svg>
  );
}

/** Pixel de l'interface (`--px`), en pixels CSS. */
function uiPixel(): number {
  return Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--px")) || 2;
}

/**
 * La frise prend toute la place libre du fronton (elle en est le cœur) : autant de cases que la largeur en
 * offre, par multiples de six (une case = un nombre entier de secondes, le soleil et l'ombre tombent juste).
 */
function WideFrieze({ tick }: { tick: number }) {
  const host = useRef<HTMLDivElement>(null);
  const [cells, setCells] = useState(90);
  useEffect(() => {
    const element = host.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      const fit = Math.floor(element.clientWidth / uiPixel() / 6) * 6;
      setCells(Math.max(60, Math.min(240, fit)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={host} className="min-w-0 flex-1 overflow-hidden">
      <OrbitFrieze tick={tick} cells={cells} />
    </div>
  );
}

/** Le fronton de la borne : le score (crédits, réputation), l'orbite, et l'état vital de la station. */
export function Hud({ state, desktop }: { state: StationState; desktop: boolean }) {
  const flow = state.flow;
  const orbit = orbitInfo(state.tick);
  const teams = teamsInfo(state);
  const battery = flow.energyCapacity > 0 ? state.energy / flow.energyCapacity : 0;
  const oxygen = flow.oxygenCapacity > 0 ? state.oxygen / flow.oxygenCapacity : 0;
  const energyTone: Tone = flow.energyShortfall > 0 ? "alert" : flow.energyIn < flow.energyOut ? "warn" : "normal";
  const oxygenTone: Tone = state.oxygen === 0 ? "alert" : flow.oxygenIn < flow.oxygenOut ? "warn" : "normal";
  const crewTone: Tone = flow.crewNeeded > flow.crew ? "warn" : "normal";
  const switchLabel = t(orbit.night ? "hud.dayIn" : "hud.nightIn", { time: formatDuration(orbit.ticksToSwitch) });

  const credits = (
    <Cell icon="credits" label={t("hud.credits")} compact={!desktop}>
      <RollingNumber
        value={state.credits}
        // Sur téléphone, le pictogramme de la pièce dit déjà « crédits ».
        format={(value) => (desktop ? `${formatNumber(value)} CR` : formatNumber(value))}
        scale={desktop ? 2 : 1}
        label={`${formatNumber(state.credits)} crédits`}
      />
    </Cell>
  );
  const reputation = (
    <Cell icon="reputation" label={t("hud.reputation")} compact={!desktop}>
      <PixelText text={formatPermyriad(state.reputation)} />
    </Cell>
  );
  const progress = levelInfo(state);
  const toNext = progress.xpTo === null ? 1 : (state.xp - progress.xpFrom) / Math.max(1, progress.xpTo - progress.xpFrom);
  const level = (
    <div data-tutorial="hud-level" className="flex">
      <Cell
        icon="level"
        label={t("hud.level")}
        compact={!desktop}
        detail={
          progress.xpTo === null
            ? t("hud.levelMax")
            : t("hud.levelDetail", { xp: formatNumber(state.xp), next: formatNumber(progress.xpTo), level: progress.level + 1 })
        }
      >
        <span className="text-accent">
          <PixelText text={String(progress.level)} label={t("hud.levelValue", { level: progress.level })} scale={desktop ? 2 : 1} />
        </span>
        {desktop ? (
          <Gauge value={toNext} color="var(--color-accent)" label={t("hud.level")} segments={8} className="w-[calc(var(--px)*36)]" />
        ) : null}
      </Cell>
    </div>
  );
  const frieze = (
    <Cell
      icon={orbit.night ? "shadow" : "sun"}
      label={orbit.night ? t("hud.night") : t("hud.day")}
      detail={switchLabel}
      compact={!desktop}
      className={desktop ? "w-full" : ""}
    >
      {desktop ? <WideFrieze tick={state.tick} /> : <OrbitFrieze tick={state.tick} cells={30} />}
      <span className={orbit.night ? "text-night" : "text-sun"}>
        <PixelText text={formatClock(orbit.ticksToSwitch)} label={false} />
      </span>
    </Cell>
  );
  const energy = (
    <Cell
      icon="energy"
      className={desktop ? "items-center" : ""}
      label={t("hud.energy")}
      tone={energyTone}
      compact={!desktop}
      detail={t("hud.energyDetail", { in: flow.energyIn, out: flow.energyOut, stored: state.energy, capacity: flow.energyCapacity })}
    >
      <Gauge
        value={battery}
        color={energyTone === "normal" ? "var(--color-sun)" : TONE_COLORS[energyTone]}
        label={t("hud.energy")}
        segments={desktop ? 10 : 8}
        className={desktop ? "w-[calc(var(--px)*46)]" : "w-[calc(var(--px)*30)]"}
      />
      {desktop ? <PixelText text={`${Math.round(battery * 100)}%`} label={false} /> : null}
    </Cell>
  );
  const oxygenCell = (
    <Cell
      icon="oxygen"
      className={desktop ? "items-center" : ""}
      label={t("hud.oxygen")}
      tone={oxygenTone}
      compact={!desktop}
      detail={t("hud.oxygenDetail", {
        in: Math.round(flow.oxygenIn / 100),
        out: Math.round(flow.oxygenOut / 100),
        percent: Math.round(oxygen * 100),
      })}
    >
      <Gauge
        value={oxygen}
        color={oxygenTone === "normal" ? "var(--color-life)" : TONE_COLORS[oxygenTone]}
        label={t("hud.oxygen")}
        segments={desktop ? 10 : 8}
        className={desktop ? "w-[calc(var(--px)*46)]" : "w-[calc(var(--px)*30)]"}
      />
      {desktop ? <PixelText text={`${Math.round(oxygen * 100)}%`} label={false} /> : null}
    </Cell>
  );
  const crew = (
    <Cell
      icon="crew"
      className={desktop ? "items-center" : ""}
      label={t("hud.crew")}
      tone={crewTone}
      detail={t("hud.crewDetail", { needed: flow.crewNeeded, crew: flow.crew })}
      compact={!desktop}
    >
      <PixelText text={`${flow.crewNeeded}/${flow.crew}`} label={false} />
    </Cell>
  );
  const teamsCell = (
    <Cell
      icon="teams"
      className={desktop ? "items-center" : ""}
      label={t("hud.teams")}
      tone={teams.free === 0 ? "warn" : "normal"}
      compact={!desktop}
      detail={t("hud.teamsDetail", { free: teams.free, total: teams.total })}
    >
      <PixelText text={`${teams.free}/${teams.total}`} label={false} />
    </Cell>
  );
  const condition =
    flow.condition < 10_000 ? (
      <Cell
        icon="condition"
      className={desktop ? "items-center" : ""}
        label={t("hud.conditionShort")}
        tone={flow.condition < 7000 ? "alert" : "warn"}
        detail={t("hud.condition", { percent: Math.floor(flow.condition / 100) })}
        compact={!desktop}
      >
        <PixelText text={formatPermyriad(flow.condition)} label={false} />
      </Cell>
    ) : null;

  if (desktop) {
    return (
      <header className="bezel pointer-events-auto mx-3 mt-3" data-tutorial="hud">
        {/* Sur un écran moyen (tablette), l'état vital passe sur une seconde ligne plutôt que de déborder. */}
        <div className="flex flex-wrap items-stretch gap-x-4 gap-y-2 px-2 py-1.5">
          <div className="hidden flex-col justify-center pr-1 text-accent 2xl:flex">
            <PixelText text={t("app.title")} />
          </div>
          <span aria-hidden="true" className="hidden w-[var(--px)] self-stretch bg-[#1c2b56] 2xl:block" />
          <div data-tutorial="hud-score" className="flex items-stretch gap-4">
            {credits}
            {reputation}
          </div>
          {level}
          <Divider />
          <div data-tutorial="hud-orbit" className="flex min-w-[calc(var(--px)*110)] flex-1">
            {frieze}
          </div>
          {/* L'état vital : chaque valeur centrée sous son libellé, assez d'air pour ne pas les lire d'un tenant. */}
          <div className="ml-auto flex items-stretch gap-7">
            {energy}
            {oxygenCell}
            {crew}
            {teamsCell}
            {condition}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="bezel pointer-events-auto pt-[max(calc(var(--px)*4),env(safe-area-inset-top))]" data-tutorial="hud">
      <div className="flex flex-col gap-2 px-0.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div data-tutorial="hud-score" className="flex items-center gap-2.5">
              {credits}
              {reputation}
            </div>
            {level}
          </div>
          <div data-tutorial="hud-orbit">{frieze}</div>
        </div>
        <div className="flex items-center justify-between gap-2">
          {energy}
          {oxygenCell}
          {crew}
          {condition ?? teamsCell}
        </div>
      </div>
    </header>
  );
}
